import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import type { JPBDateBounds } from "./JPBManager";
import { AreaUnit, AverageCalculationMethod, JPBPriceIndexSource, PriceMode, YearGrouping, type JPBReferenceProperty } from "./JPBState";
import type { RetentionShareOnMark } from "./JPBFunc";

export type JpbViewMode = "simple" | "advanced";

export type JPBAnalyzerGeometryData = {
   polygonArea: number;
   farmlandOverlapArea: number;
   farmlandOverlapPercentage: number;
   soilComposition: { [soilType: string]: number };
   /** Area-weighted average kvælstofretention (%) on farmland only. */
   averageRetentionPercent?: number | null;
   retentionShares?: RetentionShareOnMark[];
   retentionUncoveredShareOfMark?: number;
   error?: string;
};

export type JPBAnalyzerResult = {
   polygonArea: number;
   farmlandOverlapArea: number;
   farmlandOverlapPercentage: number;
   soilComposition: { [soilGroup: string]: number };
   estimatedPricePerSquareMeter: number | null;
   averageRetentionPercent?: number | null;
   retentionShares?: RetentionShareOnMark[];
   retentionUncoveredShareOfMark?: number;
   error?: string;
};

export type JPBTableSortColumn = {
   columnKey: string;
   direction: "ASC" | "DESC";
};

export type JPBSystemPriceAssumptions = {
   pricePerMeterMin: number;
   pricePerMeterMax: number;
   areaMin: number;
   areaMax: number;
   effectiveMarkOverlapThreshold: number;
};

export type JPBCustomPriceAssumptions = {
   pricePerMeterMin?: number;
   pricePerMeterMax?: number;
   areaMin?: number;
   areaMax?: number;
   effectiveMarkOverlapThreshold?: number;
   excludeBuildings?: boolean;
};

export type JPBSearchSessionState = {
   selectedLayer: number | undefined;
   bufferGeometry: any;
   bufferDistanceKm: number | null;
   bufferDistanceByLayer: Record<string, number>;
   viewMode: JpbViewMode;
   simpleResolvedAddressLine: string | null;
   simpleAddressLineResolving: boolean;
};

export type JPBFiltersSessionState = {
   checkedItems: Set<string>;
   hideUnselected: "none" | "unchecked" | "locked";
   customPriceAssumptions: JPBCustomPriceAssumptions | undefined;
   systemPriceAssumptions: JPBSystemPriceAssumptions | undefined;
   areaUnit: AreaUnit;
   priceMode: PriceMode;
   averageCalculationMethod: AverageCalculationMethod;
   priceIndexSource: JPBPriceIndexSource;
   appliedYearGrouping: YearGrouping;
   appliedCalculationDateTo: Date | null;
};

export type JPBResultsSessionState = {
   referenceProperties: JPBReferenceProperty[];
   loading: boolean;
   error: string | null;
   dateBounds: JPBDateBounds | null;
   lastFetchKey: string | null;
   settingsRefreshToken: number;
   mapRefreshToken: number;
   isGeneratingReport: boolean;
};

export type JPBAnalyzerSessionState = {
   selectedLayerId: number | undefined;
   analysisResult: JPBAnalyzerResult | null;
   isCalculating: boolean;
   isFetchingGeometry: boolean;
   geometryData: JPBAnalyzerGeometryData | null;
   farmlandIntersectionGeometry: any | null;
   jordbundskortIntersectionGeometry: any | null;
   analysisPropertyOutlineGeometry: any | null;
   hoveredSoilType: string | null;
   hoveredSoilPercentage: number | null;
   hoveredNonFarmland: boolean;
};

export type JPBTableUiSessionState = {
   visibleColumns: string[];
   sortColumns: readonly JPBTableSortColumn[];
   addressByBfe: Record<number, string>;
};

export type JPBUiSessionState = {
   isWindowMinimized: boolean;
};

export type JPBSessionState = {
   search: JPBSearchSessionState;
   filters: JPBFiltersSessionState;
   results: JPBResultsSessionState;
   analyzer: JPBAnalyzerSessionState;
   tableUi: JPBTableUiSessionState;
   ui: JPBUiSessionState;
};

type SliceKey = keyof JPBSessionState;

type SetSliceAction = {
   type: "setSlice";
   slice: SliceKey;
   value: unknown;
};

type ResetAction = {
   type: "reset";
   value: JPBSessionState;
};

type JPBSessionAction = SetSliceAction | ResetAction;
type SlicePatch<K extends SliceKey> =
   Partial<JPBSessionState[K]> |
   ((prev: JPBSessionState[K]) => Partial<JPBSessionState[K]>);

const initialVisibleColumns = [
   "select-row",
   "transactionDate",
   "area",
   "originalTotalPrice",
   "totalPrice",
   "pricePerSquareMeter"
];

const sessionRegistry = new Map<string | number, JPBSessionState>();

function cloneSet(values: Set<string>): Set<string> {
   return new Set(values);
}

function cloneState(state: JPBSessionState): JPBSessionState {
   return {
      search: {
         ...state.search,
         bufferDistanceByLayer: { ...state.search.bufferDistanceByLayer }
      },
      filters: {
         ...state.filters,
         checkedItems: cloneSet(state.filters.checkedItems),
         customPriceAssumptions: state.filters.customPriceAssumptions ? { ...state.filters.customPriceAssumptions } : undefined,
         systemPriceAssumptions: state.filters.systemPriceAssumptions ? { ...state.filters.systemPriceAssumptions } : undefined
      },
      results: {
         ...state.results,
         referenceProperties: [...state.results.referenceProperties]
      },
      analyzer: {
         ...state.analyzer
      },
      tableUi: {
         ...state.tableUi,
         visibleColumns: [...state.tableUi.visibleColumns],
         sortColumns: [...state.tableUi.sortColumns],
         addressByBfe: { ...state.tableUi.addressByBfe }
      },
      ui: {
         ...state.ui
      }
   };
}

function mergeSlice<K extends SliceKey>(
   currentSlice: JPBSessionState[K],
   patch: Partial<JPBSessionState[K]>
): JPBSessionState[K] {
   let changed = false;
   const nextSlice = { ...currentSlice } as JPBSessionState[K];
   for (const [rawKey, rawValue] of Object.entries(patch)) {
      const key = rawKey as keyof JPBSessionState[K];
      const value = rawValue as JPBSessionState[K][typeof key];
      if (!Object.is(currentSlice[key], value)) {
         changed = true;
         nextSlice[key] = value;
      }
   }
   return changed ? nextSlice : currentSlice;
}

function reducer(state: JPBSessionState, action: JPBSessionAction): JPBSessionState {
   switch (action.type) {
      case "reset":
         return cloneState(action.value);
      case "setSlice": {
         const currentSlice = state[action.slice];
         const nextSlice = typeof action.value === "function"
            ? (action.value as (prev: typeof currentSlice) => typeof currentSlice)(currentSlice)
            : action.value as typeof currentSlice;
         if (Object.is(currentSlice, nextSlice)) {
            return state;
         }
         return {
            ...state,
            [action.slice]: nextSlice
         };
      }
      default:
         return state;
   }
}

const sessionContext = createContext<{
   state: JPBSessionState;
   dispatch: React.Dispatch<JPBSessionAction>;
} | null>(null);

export function clearJPBSession(sessionKey: string | number): void {
   sessionRegistry.delete(sessionKey);
}

export function createInitialJPBSessionState(params: {
   priceIndexSource: JPBPriceIndexSource;
   areaUnit: AreaUnit;
   priceMode: PriceMode;
   averageCalculationMethod: AverageCalculationMethod;
   viewMode: JpbViewMode;
   appliedYearGrouping?: YearGrouping;
   appliedCalculationDateTo?: Date | null;
   customPriceAssumptions?: JPBCustomPriceAssumptions;
   visibleColumns?: string[];
}): JPBSessionState {
   return {
      search: {
         selectedLayer: undefined,
         bufferGeometry: null,
         bufferDistanceKm: null,
         bufferDistanceByLayer: {},
         viewMode: params.viewMode,
         simpleResolvedAddressLine: null,
         simpleAddressLineResolving: false
      },
      filters: {
         checkedItems: new Set(),
         hideUnselected: "none",
         customPriceAssumptions: params.customPriceAssumptions
            ? { ...params.customPriceAssumptions }
            : undefined,
         systemPriceAssumptions: undefined,
         areaUnit: params.areaUnit,
         priceMode: params.priceMode,
         averageCalculationMethod: params.averageCalculationMethod,
         priceIndexSource: params.priceIndexSource,
         appliedYearGrouping: params.appliedYearGrouping ?? YearGrouping.ThreeYears,
         appliedCalculationDateTo: params.appliedCalculationDateTo ?? null
      },
      results: {
         referenceProperties: [],
         loading: false,
         error: null,
         dateBounds: null,
         lastFetchKey: null,
         settingsRefreshToken: 0,
         mapRefreshToken: 0,
         isGeneratingReport: false
      },
      analyzer: {
         selectedLayerId: undefined,
         analysisResult: null,
         isCalculating: false,
         isFetchingGeometry: false,
         geometryData: null,
         farmlandIntersectionGeometry: null,
         jordbundskortIntersectionGeometry: null,
         analysisPropertyOutlineGeometry: null,
         hoveredSoilType: null,
         hoveredSoilPercentage: null,
         hoveredNonFarmland: false
      },
      tableUi: {
         visibleColumns: params.visibleColumns?.length
            ? [...params.visibleColumns]
            : [...initialVisibleColumns],
         sortColumns: [],
         addressByBfe: {}
      },
      ui: {
         isWindowMinimized: false
      }
   };
}

export function JPBSessionProvider(props: {
   sessionKey: string | number;
   initialState: JPBSessionState;
   children: React.ReactNode;
}) {
   const [state, dispatch] = useReducer(
      reducer,
      undefined,
      () => cloneState(sessionRegistry.get(props.sessionKey) ?? props.initialState)
   );

   useEffect(() => {
      sessionRegistry.set(props.sessionKey, cloneState(state));
   }, [props.sessionKey, state]);

   const value = useMemo(() => ({ state, dispatch }), [state]);

   return (
      <sessionContext.Provider value={value}>
         {props.children}
      </sessionContext.Provider>
   );
}

export function useJPBSession() {
   const value = useContext(sessionContext);
   if (!value) {
      throw new Error("useJPBSession must be used within a JPBSessionProvider");
   }
   return value;
}

export function useJPBSessionSlice<K extends SliceKey>(slice: K): [
   JPBSessionState[K],
   (value: JPBSessionState[K] | ((prev: JPBSessionState[K]) => JPBSessionState[K])) => void
] {
   const { state, dispatch } = useJPBSession();
   const setSlice = useCallback((value: JPBSessionState[K] | ((prev: JPBSessionState[K]) => JPBSessionState[K])) => {
      dispatch({ type: "setSlice", slice, value });
   }, [dispatch, slice]);
   return [state[slice], setSlice];
}

export function useJPBSessionActions() {
   const { dispatch } = useJPBSession();

   const patchSlice = useCallback(<K extends SliceKey>(slice: K, patch: SlicePatch<K>) => {
      dispatch({
         type: "setSlice",
         slice,
         value: (prev: JPBSessionState[K]) => mergeSlice(
            prev,
            typeof patch === "function" ? patch(prev) : patch
         )
      });
   }, [dispatch]);

   return useMemo(() => ({
      patchSearch: (patch: SlicePatch<"search">) => patchSlice("search", patch),
      patchFilters: (patch: SlicePatch<"filters">) => patchSlice("filters", patch),
      patchResults: (patch: SlicePatch<"results">) => patchSlice("results", patch),
      patchAnalyzer: (patch: SlicePatch<"analyzer">) => patchSlice("analyzer", patch),
      patchTableUi: (patch: SlicePatch<"tableUi">) => patchSlice("tableUi", patch),
      patchUi: (patch: SlicePatch<"ui">) => patchSlice("ui", patch),
      resetSession: (value: JPBSessionState) => dispatch({ type: "reset", value })
   }), [dispatch, patchSlice]);
}
